import { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { EmptyState, ErrorState, FreshnessBar, LoadingState } from "../components/AdminPageStates";
import MetricCard from "../components/MetricCard";
import { useCoachesAdmin } from "../hooks/useCoachesAdmin";
import { CoachAdminRow } from "../services/coachesAdminService";

function formatCurrency(value: number | null) {
  return value === null
    ? "Unavailable"
    : new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(value);
}

function servicesText(coach: CoachAdminRow) {
  return coach.servicesOffered.length > 0 ? coach.servicesOffered.join(", ") : "Unavailable";
}

function displayName(coach: CoachAdminRow) {
  return coach.displayName ?? coach.username ?? coach.email ?? coach.id;
}

export default function Coaches() {
  const navigate = useNavigate();
  const { data, error, filters, isLoading, refresh, setFilters } = useCoachesAdmin();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void refresh(filters);
  }

  if (isLoading && !data) {
    return <LoadingState message="Loading coaches." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!data) {
    return <EmptyState message="No coach data is available." />;
  }

  const activeCount = data.coaches.filter((coach) => coach.statusLabel === "Active").length;
  const coachesWithServices = data.coaches.filter((coach) => coach.servicesOffered.length > 0).length;
  const grossRevenue = data.coaches.reduce((total, coach) => total + (coach.grossRevenue ?? 0), 0);
  const estimatedEarnings = data.coaches.reduce((total, coach) => total + (coach.estimatedCoachEarnings ?? 0), 0);

  return (
    <>
      <FreshnessBar
        dataSource="users, bookings, coach_earnings, coach_payouts"
        isLive={false}
        lastUpdated={data.lastUpdated}
        onRefresh={() => {
          void refresh(filters);
        }}
      />

      <form className="filter-panel coach-filter-panel" onSubmit={handleSubmit}>
        <label>
          Date range start
          <input
            type="date"
            value={filters.startDate}
            onChange={(event) => setFilters({ ...filters, startDate: event.target.value })}
          />
        </label>
        <label>
          Date range end
          <input
            type="date"
            value={filters.endDate}
            onChange={(event) => setFilters({ ...filters, endDate: event.target.value })}
          />
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={filters.activeOnly}
            onChange={(event) => setFilters({ ...filters, activeOnly: event.target.checked })}
          />
          Active coaches
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={filters.withServices}
            onChange={(event) => setFilters({ ...filters, withServices: event.target.checked })}
          />
          With services
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={filters.withEarnings}
            onChange={(event) => setFilters({ ...filters, withEarnings: event.target.checked })}
          />
          With earnings
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={filters.noBookings}
            onChange={(event) => setFilters({ ...filters, noBookings: event.target.checked })}
          />
          No bookings
        </label>
        <button type="submit" className="primary-button">
          Apply
        </button>
      </form>

      <div className="overview-grid">
        <MetricCard title="Coach profiles" value={String(data.coaches.length)} note="Bounded read from users coach signals." />
        <MetricCard title="Active coaches" value={String(activeCount)} note="Approved, feedback-enabled, and not suspended." />
        <MetricCard title="With services" value={String(coachesWithServices)} note="Derived from feedback service profile fields." />
        <MetricCard title="Gross revenue" value={formatCurrency(grossRevenue)} note="Sum of backend-created coach_earnings amount_usd." />
        <MetricCard title="Estimated earnings" value={formatCurrency(estimatedEarnings)} note="Sum of backend-created net_amount_usd." />
      </div>

      {data.coaches.length === 0 ? (
        <EmptyState message="No coaches match the current filters." />
      ) : (
        <div className="table-shell">
          <table className="admin-table coaches-table">
            <thead>
              <tr>
                <th>Coach</th>
                <th>Services offered</th>
                <th>Status</th>
                <th>Total bookings</th>
                <th>Gross revenue</th>
                <th>Chor fee</th>
                <th>Estimated earnings</th>
                <th>Payout status</th>
              </tr>
            </thead>
            <tbody>
              {data.coaches.map((coach) => (
                <tr
                  key={coach.id}
                  onClick={() => navigate(`/coaches/${coach.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      navigate(`/coaches/${coach.id}`);
                    }
                  }}
                  tabIndex={0}
                >
                  <td>
                    <strong>{displayName(coach)}</strong>
                    <span>{coach.email ?? "Email unavailable"}</span>
                  </td>
                  <td>{servicesText(coach)}</td>
                  <td>{coach.statusLabel}</td>
                  <td>{coach.totalBookings ?? "Unavailable"}</td>
                  <td>{formatCurrency(coach.grossRevenue)}</td>
                  <td>{formatCurrency(coach.chorFee)}</td>
                  <td>{formatCurrency(coach.estimatedCoachEarnings)}</td>
                  <td>{coach.payoutStatus ?? "Unavailable"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
